import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RoastExtraColumnKey, RoastProfileEditableKey, RoastProfileRow, RoastStageState } from './types';
import { RoastProfileChart } from './RoastProfileChart';
import { RoastProfileTable } from './RoastProfileTable';
import { usePreservedHeight } from './usePreservedHeight';
import {
  buildEmptyRowAtIndex,
  calculateRorForRows,
  diffProfileAgainstBaseline,
  ensureSingleTrailingRow,
  rowHasData,
  stripTrailingEmptyRows,
} from './RoastProfileUtils';
import notificationSound from '../../../assets/sounds/notification.mp3';
import { subscribeStopwatchTick, getStopwatchSecondsSnapshot } from './stopwatchTick';
import { useRoastChartData } from './useRoastChartData';

interface Props {
  seconds: number;
  stage: string;
  onDataChange?: (data: RoastProfileRow[]) => void;
  mode?: 'roast' | 'recipe';
  initialProfile?: RoastProfileRow[];
  readOnly?: boolean;
  stackLayout?: boolean;
  preserveHeightGroupId?: string;
  useStopwatchMode?: boolean;
  stopwatchSlot?: React.ReactNode;
  stages?: RoastStageState[];
  /**
   * The profile as originally imported. Supplying it turns on edit tracking: cells
   * that drift from it are marked, and the table gains per-cell, per-column and
   * whole-profile revert. Leave undefined to keep the table untracked.
   */
  baselineProfile?: RoastProfileRow[];
}

export const RoastProfileSection: React.FC<Props> = React.memo(({
  seconds,
  stage,
  onDataChange,
	mode = "roast",
  initialProfile = [],
  readOnly = false,
	stackLayout = false,
  preserveHeightGroupId,
  useStopwatchMode = false,
  stopwatchSlot,
  stages,
  baselineProfile,
}) => {
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastNotifiedBucketRef = useRef<number>(Math.floor(seconds / 30));
  // Signature formula must stay in sync with `initialProfileSignature` below;
  // any mismatch breaks the round-trip detection that prevents the seconds-effect's
  // empty lookahead rows from being deleted by the initialProfile sync effect.
  const computeProfileSignature = useCallback(
    (profile: RoastProfileRow[]) =>
      profile.map(p => `${p.seconds}:${p.bean ?? ''}:${p.fire ?? ''}:${p.airflow ?? ''}:${p.airTemp ?? ''}:${p.drumTemp ?? ''}:${p.environmentTemp ?? ''}:${p.drumRpm ?? ''}`).join('|'),
    []
  );
  const lastEmittedSignatureRef = useRef<string | null>(null);
  const [rows, setRows] = useState<RoastProfileRow[]>(() => {
    let baseRows: RoastProfileRow[] = [];

    if (initialProfile.length > 0) {
      if (mode === "recipe" && !useStopwatchMode) {
        baseRows = ensureSingleTrailingRow(initialProfile);
      } else {
        baseRows = stripTrailingEmptyRows(initialProfile);
      }
    } else if (mode === "recipe" && !useStopwatchMode) {
      baseRows = [buildEmptyRowAtIndex(0)];
    }

    if (baseRows.length === 0) {
      return baseRows;
    }

    return calculateRorForRows(baseRows);
  });
  const [extraColumns, setExtraColumns] = useState<RoastExtraColumnKey[]>(() => {
		const hasAirTemp = initialProfile.some((r) => r.airTemp != null);
		const hasDrumTemp = initialProfile.some((r) => r.drumTemp != null);
		const hasEnvironmentTemp = initialProfile.some((r) => r.environmentTemp != null);
		const hasDrumRpm = initialProfile.some((r) => r.drumRpm != null);
		const cols: RoastExtraColumnKey[] = [];
		if (hasAirTemp) cols.push("airTemp");
		if (hasDrumTemp) cols.push("drumTemp");
		if (hasEnvironmentTemp) cols.push("environmentTemp");
		if (hasDrumRpm) cols.push("drumRpm");
		return cols;
	});

  const handleExtraColumnsChange = useCallback((newCols: RoastExtraColumnKey[]) => {
    const removedCols = extraColumns.filter(col => !newCols.includes(col));

    setExtraColumns(newCols);

    if (removedCols.length > 0) {
      setRows(prevRows => prevRows.map(row => {
        let hasChanges = false;
        const newRow = { ...row };
        removedCols.forEach(col => {
          if (newRow[col] !== null && newRow[col] !== undefined) {
            newRow[col] = null;
            hasChanges = true;
          }
        });
        return hasChanges ? newRow : row;
      }));
    }
  }, [extraColumns]);

  // Notify parent of changes (without trailing empty rows). Record the signature
  // of what we sent so the sync effect below can recognise the parent echoing
  // it back and skip resetting `rows` (which would drop the seconds-effect's
  // empty lookahead row, e.g. the 00:30 row vanishing when typing in 00:00).
  useEffect(() => {
    const effectiveRows = stripTrailingEmptyRows(rows);
    lastEmittedSignatureRef.current = computeProfileSignature(effectiveRows);
    onDataChange?.(effectiveRows);
  }, [rows, onDataChange, computeProfileSignature]);

  useEffect(() => {
    notificationAudioRef.current = new Audio(notificationSound);
    notificationAudioRef.current.preload = 'auto';
  }, []);

  // Subscribe directly to the stopwatch tick so notifications fire inside the
  // rAF callback (no waiting for React commit). Keeps the audio cue aligned
  // with the live timer even when the form tree is mid re-render.
  useEffect(() => {
    if (!useStopwatchMode || readOnly) return;

    // Reset bucket tracker whenever this effect activates so the first 30s
    // crossing after start/unpause always notifies.
    lastNotifiedBucketRef.current = Math.floor(getStopwatchSecondsSnapshot() / 30);

    const unsubscribe = subscribeStopwatchTick((liveSeconds) => {
      if (liveSeconds <= 0) {
        lastNotifiedBucketRef.current = 0;
        return;
      }
      const currentBucket = Math.floor(liveSeconds / 30);
      if (currentBucket > lastNotifiedBucketRef.current) {
        lastNotifiedBucketRef.current = currentBucket;
        const audio = notificationAudioRef.current;
        if (audio) {
          audio.currentTime = 0;
          void audio.play().catch(() => {});
        }
      }
    });
    return unsubscribe;
  }, [readOnly, useStopwatchMode]);

  useEffect(() => {
    if (!useStopwatchMode || readOnly) return;

    let id: number;
    // RESET if seconds is 0 and stage is idle (Restart case)
    if (seconds === 0 && stage === 'idle') {
      const initialRows: RoastProfileRow[] = [];

      for (let i = 0; i <= 1; i++) {
        const sec = i * 30;
        const m = Math.floor(sec / 60);
        const s = sec % 60;

        initialRows.push({
          time: `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`,
          seconds: sec,
          bean: null,
          ror: null,
          fire: null,
          airflow: null,
        });
      }

      id = window.setTimeout(() => setRows(initialRows), 0);
      return () => window.clearTimeout(id);
    }

    // Generate expected 30s-interval rows that should exist up to the current time
    const maxExpectedSec = (Math.floor(seconds / 30) + 1) * 30;

    id = window.setTimeout(() => setRows((prev) => {
      // Build a set of existing 30s-interval seconds
      const existingSeconds = new Set(prev.map(r => r.seconds));

      const newRows: RoastProfileRow[] = [];
      for (let sec = 0; sec <= maxExpectedSec; sec += 30) {
        if (!existingSeconds.has(sec)) {
          const m = Math.floor(sec / 60);
          const s = sec % 60;
          newRows.push({
            time: `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`,
            seconds: sec,
            bean: null,
            ror: null,
            fire: null,
            airflow: null,
          });
        }
      }

      if (newRows.length === 0) return prev;

      const next = [...prev, ...newRows].sort((a, b) => a.seconds - b.seconds);
      return next;
    }), 0);
    return () => window.clearTimeout(id);
  }, [seconds, stage, useStopwatchMode, readOnly]);

  useEffect(() => {
    if (mode !== "recipe") return;
    if (useStopwatchMode) return;

    const id = window.setTimeout(() => setRows((prev) => ensureSingleTrailingRow(prev)), 0);
    return () => window.clearTimeout(id);
  }, [mode, useStopwatchMode]);

  // Sync rows when caller-provided initialProfile content changes (e.g. switching work orders,
  // loading a recipe snapshot). Replaces the previous remount-via-key pattern on the parent.
  // Uses a cheap derived signature to detect real content changes without JSON.stringify overhead.
  const initialProfileSignature = useMemo(
    () => computeProfileSignature(initialProfile),
    [initialProfile, computeProfileSignature]
  );

  const prevSignatureRef = useRef(initialProfileSignature);
  useEffect(() => {
    if (prevSignatureRef.current === initialProfileSignature) return;
    // If the parent is just echoing back what we just emitted (the round-trip
    // produced by the rows effect above), don't rebuild rows from initialProfile.
    // Doing so would strip trailing empty rows added by the seconds-effect — the
    // root cause of row n+1 disappearing when the user types into row n.
    if (lastEmittedSignatureRef.current === initialProfileSignature) {
      prevSignatureRef.current = initialProfileSignature;
      return;
    }
    prevSignatureRef.current = initialProfileSignature;

    let baseRows: RoastProfileRow[] = [];
    if (initialProfile.length > 0) {
      if (mode === "recipe" && !useStopwatchMode) {
        baseRows = ensureSingleTrailingRow(initialProfile);
      } else {
        baseRows = stripTrailingEmptyRows(initialProfile);
      }
    } else if (mode === "recipe" && !useStopwatchMode) {
      baseRows = [buildEmptyRowAtIndex(0)];
    }
    const nextRows = baseRows.length === 0 ? baseRows : calculateRorForRows(baseRows);

    // Re-derive extraColumns from the new initialProfile, mirroring the useState
    // initializer logic exactly. Previously the implicit remount (key-based) reset
    // this state; now we must sync it explicitly when initialProfile changes.
    const hasAirTemp = initialProfile.some(r => r.airTemp != null);
    const hasDrumTemp = initialProfile.some(r => r.drumTemp != null);
    const hasEnvironmentTemp = initialProfile.some(r => r.environmentTemp != null);
    const hasDrumRpm = initialProfile.some(r => r.drumRpm != null);
    const cols: RoastExtraColumnKey[] = [];
    if (hasAirTemp) cols.push('airTemp');
    if (hasDrumTemp) cols.push('drumTemp');
    if (hasEnvironmentTemp) cols.push('environmentTemp');
    if (hasDrumRpm) cols.push('drumRpm');

    const id = window.setTimeout(() => {
      setRows(nextRows);
      setExtraColumns(cols);
    }, 0);
    return () => window.clearTimeout(id);
  }, [initialProfileSignature, initialProfile, mode, useStopwatchMode]);

  /* ================= UPDATE CELL ================= */

  const updateCell = (
    index: number,
    key: keyof Omit<RoastProfileRow, "time" | "seconds">,
    value: string
  ) => {
    if (readOnly) return;
    const num = value === "" ? null : Number(value);
    if (num !== null && isNaN(num)) return;

    setRows((prev) => {
      let copy = [...prev];
      copy[index] = { ...copy[index], [key]: num };

      if (!useStopwatchMode && mode === "recipe") {
        const isLastRow = index === copy.length - 1;
        if (isLastRow && rowHasData(copy[index])) {
          const nextIndex = copy.length;
          copy.push(buildEmptyRowAtIndex(nextIndex));
        }
      }

      if (key === "bean") {
        copy = calculateRorForRows(copy);
      }

      return copy;
    });
  };

  /* ================= EDIT TRACKING ================= */

  const edits = useMemo(
    () => (baselineProfile ? diffProfileAgainstBaseline(rows, baselineProfile) : undefined),
    [rows, baselineProfile]
  );

  const baselineBySeconds = useMemo(
    () => new Map((baselineProfile ?? []).map(r => [r.seconds, r])),
    [baselineProfile]
  );

  const resetCell = useCallback(
    (index: number, key: RoastProfileEditableKey) => {
      if (readOnly) return;

      setRows(prev => {
        const row = prev[index];
        if (!row) return prev;

        const restored = baselineBySeconds.get(row.seconds)?.[key] ?? null;
        if ((row[key] ?? null) === restored) return prev;

        const copy = [...prev];
        copy[index] = { ...row, [key]: restored };
        return key === 'bean' ? calculateRorForRows(copy) : copy;
      });
    },
    [baselineBySeconds, readOnly]
  );

  const resetField = useCallback(
    (key: RoastProfileEditableKey) => {
      if (readOnly) return;

      setRows(prev => {
        let changed = false;
        const copy = prev.map(row => {
          const restored = baselineBySeconds.get(row.seconds)?.[key] ?? null;
          if ((row[key] ?? null) === restored) return row;
          changed = true;
          return { ...row, [key]: restored };
        });

        if (!changed) return prev;
        return key === 'bean' ? calculateRorForRows(copy) : copy;
      });
    },
    [baselineBySeconds, readOnly]
  );

  const resetAll = useCallback(() => {
    if (readOnly || !baselineProfile) return;

    // Same shaping as the mount-time initializer, so a reset lands exactly on the
    // profile as imported — including the trailing entry row in recipe mode.
    const baseRows =
      mode === 'recipe' && !useStopwatchMode
        ? ensureSingleTrailingRow(baselineProfile)
        : stripTrailingEmptyRows(baselineProfile);

    setRows(baseRows.length === 0 ? baseRows : calculateRorForRows(baseRows));
  }, [baselineProfile, mode, readOnly, useStopwatchMode]);

  /* ================= ADD MANUAL ROW ================= */

  const addManualRow = useCallback((timeStr: string) => {
    if (readOnly) return;

    const [mm, ss] = timeStr.split(':').map(Number);
    const totalSeconds = mm * 60 + ss;

    setRows((prev) => {
      // Prevent duplicates
      if (prev.some(r => r.seconds === totalSeconds)) return prev;

      // Find nearest row with a bean (temp) value
      let nearestBean: number | null = null;
      let closestDiff = Infinity;
      for (const r of prev) {
        if (r.bean !== null && r.bean !== undefined) {
          const diff = Math.abs(r.seconds - totalSeconds);
          if (diff < closestDiff) {
            closestDiff = diff;
            nearestBean = r.bean;
          }
        }
      }

      const newRow: RoastProfileRow = {
        time: timeStr,
        seconds: totalSeconds,
        bean: nearestBean ?? null,
        ror: null,
        fire: null,
        airflow: null,
      };

      const next = [...prev, newRow].sort((a, b) => a.seconds - b.seconds);
      return calculateRorForRows(next);
    });
  }, [readOnly]);

  /* ================= DELETE MANUAL ROW ================= */

  const deleteManualRow = useCallback((index: number) => {
    if (readOnly) return;

    setRows((prev) => {
      const row = prev[index];
      // Only allow deleting manual rows (non-30s boundary)
      if (!row || row.seconds % 30 === 0) return prev;

      const next = prev.filter((_, i) => i !== index);
      return calculateRorForRows(next);
    });
  }, [readOnly]);

  /* ================= CHART DATA ================= */

  const {
    chartData,
    xTicks,
    legendItems,
    tempAxisMax,
    tempTicks,
    rightAxisMax,
    rightTicks,
    phaseAreas,
    stageMarkers,
    hasAirTempData,
    hasDrumTempData,
    hasEnvironmentTempData,
    hasDrumRpmData,
  } = useRoastChartData({ rows, stages, extraColumns });

  const { ref: tableContainerRef, minHeight } = usePreservedHeight(preserveHeightGroupId);

  // Auto-scroll to bottom when rows length increases
  useEffect(() => {
    if (!tableContainerRef.current) return;
    const el = tableContainerRef.current;
    // Use setTimeout to ensure DOM has updated
    const id = window.setTimeout(() => {
      el.scrollTop = el.scrollHeight;
    }, 0);
    return () => window.clearTimeout(id);
  }, [rows.length, tableContainerRef]);

  return (
    <div
      className={stackLayout ? 'grid grid-cols-1 gap-6 mt-8' : 'grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-6 mt-8'}
      data-test-id="roast-profile-section"
    >
      {stackLayout ? (
        <>
          {stopwatchSlot ? <div className="min-w-0" data-test-id="roast-profile-stopwatch-slot">{stopwatchSlot}</div> : null}
          <RoastProfileChart
            chartData={chartData}
            legendItems={legendItems}
            phaseAreas={phaseAreas}
            xTicks={xTicks}
            tempAxisMax={tempAxisMax}
            tempTicks={tempTicks}
            rightAxisMax={rightAxisMax}
            rightTicks={rightTicks}
            hasAirTempData={hasAirTempData}
            hasDrumTempData={hasDrumTempData}
            hasEnvironmentTempData={hasEnvironmentTempData}
            hasDrumRpmData={hasDrumRpmData}
            stageMarkers={stageMarkers}
          />
          <RoastProfileTable
            rows={rows}
            readOnly={readOnly}
            extraColumns={extraColumns}
            onExtraColumnsChange={handleExtraColumnsChange}
            updateCell={updateCell}
            tableContainerRef={tableContainerRef}
            minHeight={minHeight}
            onAddManualRow={addManualRow}
            onDeleteManualRow={deleteManualRow}
            edits={edits}
            onResetCell={resetCell}
            onResetField={resetField}
            onResetAll={resetAll}
          />
        </>
      ) : (
        <>
          <div className="order-2 md:order-1 min-w-0">
            <RoastProfileTable
              rows={rows}
              readOnly={readOnly}
              extraColumns={extraColumns}
              onExtraColumnsChange={handleExtraColumnsChange}
              updateCell={updateCell}
              tableContainerRef={tableContainerRef}
              minHeight={minHeight}
              onAddManualRow={addManualRow}
              onDeleteManualRow={deleteManualRow}
              edits={edits}
              onResetCell={resetCell}
              onResetField={resetField}
              onResetAll={resetAll}
            />
          </div>
          <div className="order-1 md:order-2 min-w-0">
            <RoastProfileChart
              chartData={chartData}
              legendItems={legendItems}
              phaseAreas={phaseAreas}
              xTicks={xTicks}
              tempAxisMax={tempAxisMax}
              tempTicks={tempTicks}
              rightAxisMax={rightAxisMax}
              rightTicks={rightTicks}
              hasAirTempData={hasAirTempData}
              hasDrumTempData={hasDrumTempData}
              hasEnvironmentTempData={hasEnvironmentTempData}
              hasDrumRpmData={hasDrumRpmData}
              stageMarkers={stageMarkers}
            />
          </div>
        </>
      )}
    </div>
  );
});
